import React, { useEffect, useMemo, useState } from "react";
import { usePortfolioPreview, PreviewSlot } from "../../lib/hooks/useProjects";

export default function PortfolioPreview() {
  const { data: slots, loading, error } = usePortfolioPreview();
  const [active, setActive] = useState(0);

  // Only keep the first 3 (seed guarantees 3); guard against empty.
  const items = useMemo<PreviewSlot[]>(() => (slots ?? []).slice(0, 3), [slots]);
  const len = items.length;

  // Auto-advance every ~10s
  useEffect(() => {
    if (len < 2) return;
    const id = setInterval(() => setActive((i) => (i + 1) % len), 20000);
    return () => clearInterval(id);
  }, [len]);

  const goPrev = () => setActive((i) => (i - 1 + len) % len);
  const goNext = () => setActive((i) => (i + 1) % len);

  // --- Touch swipe (horizontal, scroll-friendly) -----------------------------
  function useSwipe(onLeft: () => void, onRight: () => void) {
    const startX = React.useRef<number | null>(null);
    const startY = React.useRef<number | null>(null);
    const activeSwipe = React.useRef(false);

    // Min horizontal distance to count as a swipe
    const SWIPE_THRESHOLD_PX = 40;
    // Max vertical/horizontal slope allowed (prevents fighting page scroll)
    const SWIPE_SLOPE_LIMIT = 0.6;

    const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
      const t = e.touches[0];
      startX.current = t.clientX;
      startY.current = t.clientY;
      activeSwipe.current = true;
    };
    const onTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
      if (!activeSwipe.current || startX.current == null || startY.current == null) return;
      const t = e.touches[0];
      const dx = t.clientX - startX.current;
      const dy = t.clientY - startY.current;
      // If the user is clearly swiping horizontally, stop vertical scroll jitter.
      if (Math.abs(dx) > Math.abs(dy) * 1.2) e.preventDefault();
    };
    const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = (e) => {
      if (!activeSwipe.current || startX.current == null || startY.current == null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX.current;
      const dy = t.clientY - startY.current;

      activeSwipe.current = false;
      startX.current = null;
      startY.current = null;

      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      if (absDx < SWIPE_THRESHOLD_PX || absDy / absDx > SWIPE_SLOPE_LIMIT) return;

      // Left swipe → next, right swipe → prev
      dx < 0 ? onLeft() : onRight();
    };

    return { onTouchStart, onTouchMove, onTouchEnd };
  }

  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe(goNext, goPrev);

  // Map each item to a visual slot: left, center, right (only 3 shown)
  const posClass = (idx: number) => {
    if (len === 0) return "opacity-0";
    const rel = (idx - active + len) % len; // 0=center, 1=right, len-1=left

    if (rel === 0) {
      // center: full size
      return "z-20 translate-x-0 scale-[0.74] blur-0 opacity-100";
    }
    if (rel === 1) {
      // right (back): smaller
      return "z-10 translate-x-[35%] scale-[0.56] blur-[2px] opacity-85";
    }
    if (rel === len - 1) {
      // left (back): smaller
      return "z-10 -translate-x-[35%] scale-[0.56] blur-[2px] opacity-85";
    }
    return "opacity-0 pointer-events-none";
  };

  return (
    <section id="portfolio" className="relative pt-20 pb-36 bg-[#080d1b] overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="text-center mb-4 md:mb-0">
          <h1 className="text-5xl font-bold text-[#dee3f2] mb-2">
            See What We Can Do <span className="text-[#2B61DE]">For You</span>
          </h1>
          <h6 className="text-sm text-[#B1BFE4]">The Bracket &amp; Brace Collection</h6>
        </div>

        {/* Loading / error states */}
        {error && (
          <div className="text-red-400 bg-red-950/30 border border-red-600/40 rounded-xl p-4">
            Failed to load portfolio preview.
          </div>
        )}
        {loading && !len && (
          <div className="grid md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-72 bg-gray-200/10 rounded-2xl animate-pulse" />
            ))}
          </div>
        )}

        {/* Carousel viewport */}
        {!!len && (
          <div className="relative h-[34rem] md:h-[38rem] lg:h-[40rem]">
            {/* gradient floor for depth */}
            <div className="pointer-events-none absolute inset-x-10 bottom-6 h-40 rounded-[100px] bg-gradient-to-r from-[#96ade4] to-[#1d3c8c] opacity-20 blur-[2px]" />

            <div
              className="relative h-full flex items-center justify-center perspective-[1200px]"
              aria-roledescription="carousel"
              aria-label="Featured projects"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {items.map((slot, idx) => (
                <Card
                  key={slot.id}
                  slot={slot}
                  className={`
                    absolute h-full
                    w-[96%] md:w-[clamp(960px,72vw,1240px)]
                    origin-center
                    transition-all duration-700 ease-out transform-gpu
                    ${posClass(idx)}
                  `}
                />
              ))}

              {/* Controls + Long CTA */}
              <div className="absolute inset-x-0 bottom-0 translate-y-10 md:translate-y-14 xl:translate-y-24 flex flex-col items-center gap-6 z-30">
                {/* Controls */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    aria-label="Previous"
                    onClick={goPrev}
                    className="rounded-full border border-white/20 text-white/80 hover:text-white hover:border-white/40 px-3 py-1 text-sm"
                  >
                    ‹
                  </button>
                  <div className="flex gap-2">
                    {items.map((_, i) => (
                      <button
                        key={i}
                        aria-label={`Go to slide ${i + 1}`}
                        onClick={() => setActive(i)}
                        className={`h-2 w-2 rounded-full transition ${i === active ? "bg-white" : "bg-white/30"}`}
                      />
                    ))}
                  </div>
                  <button
                    aria-label="Next"
                    onClick={goNext}
                    className="rounded-full border border-white/20 text-white/80 hover:text-white hover:border-white/40 px-3 py-1 text-sm"
                  >
                    ›
                  </button>
                </div>

                {/* Long CTA (matches “Learn more” look) */}
                <a
                  href="/portfolio"
                  className="inline-block px-5 py-3 rounded-xl bg-white text-black font-semibold
                             hover:opacity-90 transition shadow-[0_10px_20px_rgba(0,0,0,0.25)] text-center"
                >
                  Browse our catalogue.
                  <br />
                  <span className="opacity-80">Discover our success stories.</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/** 1:1 image with smart positioning:
 *  - Parent controls the square size (h-full + aspect-square).
 *  - We absolutely fill that square.
 */
function SquareMedia({ src, alt }: { src: string; alt: string }) {
  const [pos, setPos] = useState<"center" | "left-top">("center");
  const onLoad: React.ReactEventHandler<HTMLImageElement> = (e) => {
    const { naturalWidth: w = 1, naturalHeight: h = 1 } = e.currentTarget;
    const r = w / h;
    setPos(r >= 0.9 && r <= 1.1 ? "center" : "left-top");
  };
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onLoad={onLoad}
      className={`absolute inset-0 h-full w-full object-cover ${
        pos === "center" ? "object-center" : "object-[left_top]"
      }`}
    />
  );
}

function Card({
  slot,
  className = "",
}: {
  slot: PreviewSlot;
  className?: string;
}) {
  const title = slot.override_title || slot.project?.title || "Untitled";
  const desc = slot.override_description || slot.project?.description || "";
  const heroImg = slot.media?.find((m) => m.media_type === "image")?.url;
  const projectHref = `/portfolio#${slot.project?.id ?? ""}`;

  // Make whole card navigate (mouse + keyboard)
  const go = () => (projectHref ? (window.location.href = projectHref) : undefined);
  const onKey: React.KeyboardEventHandler = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      go();
    }
  };

  return (
    <article
      role="link"
      tabIndex={0}
      aria-label={`Open portfolio: ${title}`}
      onClick={go}
      onKeyDown={onKey}
      className={`h-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-[40px] bg-[#1a3c8a] text-[#dee3f2] shadow-[0_4px_50px_0_rgb(0_0_0,/_0.3)] border border-white/5 overflow-hidden ${className}`}
      style={{ transformStyle: "preserve-3d" }}
    >
      {/* STRICT two-column layout:
         - left: exact square (height = card height, width = height)
         - right: fills the remaining width for copy */}
      <div className="h-full w-full flex flex-col md:flex-row">
        {/* Left media square */}
        <div className="relative h-auto sm:w-full md:w-auto md:h-full aspect-[16/9] md:aspect-square flex-shrink-0 overflow-hidden bg-black/20">
          {heroImg ? (
            <SquareMedia src={heroImg} alt={title} />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <span className="text-white/50 text-sm">No image</span>
            </div>
          )}
        </div>

        {/* Right text pane */}
        <div
          className="
              w-full min-w-0 min-h-0 max-h-full
              p-6 md:p-8 pr-7 md:pr-9
              flex flex-col gap-4 md:gap-6
              overflow-y-auto overflow-x-visible
              break-words hyphens-auto
              md:flex-[0_0_clamp(16.5rem,24vw,34rem)]
            "
        >
          <h3 className="font-extrabold text-[#b3c1e6] leading-tight  text-[clamp(1.5rem,2.2vw,2.75rem)] md:text-[clamp(1.75rem,2.1vw,3rem)] w-full max-w-full">
            {title}
          </h3>
          <p className="text-[#dee3f2]/90 leading-relaxed break-words hyphens-auto text-[clamp(0.95rem,1.1vw,1.15rem)] md:text-[clamp(1rem,1.05vw,1.2rem)]">
            {desc}
          </p>

          {slot.testimonial?.quote && (
            <blockquote className="text-[#efc849] italic border-l-2 border-[#efc849]/50 pl-4 break-words hyphens-auto leading-relaxed text-[clamp(0.95rem,1.05vw,1.1rem)] md:text-[clamp(1rem,1vw,1.2rem)]">
              “{slot.testimonial.quote}”
              {slot.testimonial.attribution ? (
                <span className="not-italic text-[#dee3f2]/70"> — {slot.testimonial.attribution}</span>
              ) : null}
            </blockquote>
          )}
        </div>
      </div>
    </article>
  );
}
